import React, { useState, useEffect } from 'react';

import { validateName, isValidPostalCode } from 'utils/validates';
import InputGeneral from 'components/common/FormControls/FormInputs';
import FormSelector from 'components/common/FormControls/FormSelector';
import ButtonMedium from 'components/common/Buttons/ButtonMedium';
import { RxProviderOptions, usaStates } from '../utils';
import './styles.scss';
import { ILocationFormData } from '../AddLocationButton';

export interface IProps {
  clearFormData: boolean;
  handleSubmit: (e: any) => void;
}

export const initialFormData: ILocationFormData = {
  location: {
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    country: 'US',
    district: '',
    postalCode: '',
    externalRef: '',
  },
};

export const postalCodeErrorMessage =
  'US postal codes must have the format 00000 or 00000-0000.';

export const emptyFieldErrorMessage = (field: string): string =>
  `${field} must not be empty.`;

export const stateOptions = usaStates.map((state) => ({
  id: state.abbrevitaion,
  name: state.abbrevitaion,
}));

export const AddLocationForm = (props: IProps) => {
  const { handleSubmit, clearFormData } = props;
  const [formData, setFormData] = useState<ILocationFormData>({
    location: { ...initialFormData.location },
  });
  const [error, setError] = useState('');
  const [disableNext, setDisableNext] = useState(true);

  const validateFormData = (): boolean => {
    const {
      location: { name, addressLine1, city, district, postalCode },
    } = formData;
    setError('');
    let error = '';
    let validity = true;

    if (!isValidPostalCode(postalCode)) {
      validity = false;
      error = postalCodeErrorMessage;
    }

    const stringValidations = { name, addressLine1, city, district };
    Object.entries(stringValidations).forEach(([field, value]) => {
      if (!validateName(value || '')) {
        validity = false;
        error = `${error} \n${emptyFieldErrorMessage(field)}`;
      }
    });
    setError(error);
    return validity;
  };

  const updateProperty = (fieldName: string, value: any) => {
    // Allows for changes in nested object structure.
    fieldName.split('.').reduce((prev: any, curr: any, index, arr) => {
      const keys = Object.keys(prev);
      if (keys.includes(curr) && arr.length - 1 === index) prev[curr] = value;
      return prev[curr];
    }, formData);
    setFormData({ ...formData });
  };

  const handleInputChange =
    (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateProperty(fieldName, event.target.value);
      if (error) validateFormData();
    };

  const handleDropdownChange =
    (fieldName: string) => (item: { name: string; value: string }) => {
      updateProperty(fieldName, item.value);
      if (error) validateFormData();
    };

  const onClickNext = () => {
    const valid = validateFormData();
    if (valid) {
      handleSubmit(formData);
    }
  };

  useEffect(() => {
    if (clearFormData) {
      setFormData({ location: { ...initialFormData.location } });
      setError('');
    }
  }, [clearFormData]);

  useEffect(() => {
    if (
      formData.location?.name === '' ||
      formData.location?.addressLine1 === '' ||
      formData.location?.city === '' ||
      formData.location?.district === '' ||
      formData.location?.postalCode === ''
    ) {
      setDisableNext(true);
    } else {
      setDisableNext(false);
    }
  }, [formData]);

  return (
    <div data-testid="add-location-form">
      <div styleName="error">{error}</div>
      <div>
        <InputGeneral
          required
          label="Location Name"
          name="name"
          testidProp="add-location-input-name"
          value={formData.location?.name}
          onChange={handleInputChange('location.name')}
        />
        <InputGeneral
          required
          label="Location Address"
          name="addressLine1"
          testidProp="add-location-input-addressLine1"
          value={formData.location?.addressLine1}
          onChange={handleInputChange('location.addressLine1')}
        />
        <InputGeneral
          label="Location Address Line 2 (Optional)"
          name="addressLine2"
          testidProp="add-location-input-addressLine2"
          value={formData.location?.addressLine2}
          onChange={handleInputChange('location.addressLine2')}
        />
        <InputGeneral
          required
          label="City"
          name="city"
          testidProp="add-location-input-city"
          value={formData.location?.city}
          onChange={handleInputChange('location.city')}
        />
        <FormSelector
          required
          label="State"
          name="district"
          testidProp="add-location-input-district"
          optionFields={stateOptions}
          value={formData.location?.district}
          fullWidth={false}
          dispatchToState={handleDropdownChange('location.district')}
        />
        <br />
        <InputGeneral
          required
          label="ZIP code"
          name="postalCode"
          testidProp="add-location-input-postal-code"
          value={formData.location?.postalCode}
          onChange={handleInputChange('location.postalCode')}
          fullWidth={false}
        />
        <br />
        <FormSelector
          required
          fullWidth
          label="RX Provider"
          name="rxProvider"
          testidProp="add-location-input-rx-provider"
          optionFields={RxProviderOptions}
          value={RxProviderOptions[0].id}
        />
        <InputGeneral
          label="PearConnect ID"
          name="externalRef"
          testidProp="add-location-input-external-ref"
          value={formData.location?.externalRef}
          onChange={handleInputChange('location.externalRef')}
        />
        <ButtonMedium
          disabled={error.length !== 0 || disableNext}
          buttonRole={'primary'}
          width={'100%'}
          margin={'15px 0'}
          handleClick={onClickNext}
        >
          NEXT
        </ButtonMedium>
      </div>
    </div>
  );
};

export default AddLocationForm;
